import { LitElement, css, html, nothing } from "lit";
import type { TemplateResult } from "lit";
import type { Feed, FeedCandidate } from "@newsfeed/shared";
import type { ApiClient } from "../api.js";
import { sharedStyles } from "../styles.js";

type View =
  "idle" | "discovering" | "discoveryError" | "noFeeds" | "candidates";

/** Add-feed modal: paste a URL, discover its feeds, subscribe to one. */
export class NfAddFeedDialog extends LitElement {
  static override properties = {
    api: { attribute: false },
    feeds: { attribute: false },
    view: { state: true },
    candidates: { state: true },
    error: { state: true },
    url: { state: true },
    busyUrl: { state: true },
  };

  api?: ApiClient;
  feeds: Feed[] = [];
  view: View = "idle";
  candidates: FeedCandidate[] = [];
  error = "";
  url = "";
  busyUrl: string | null = null;

  static override styles = [
    sharedStyles,
    css`
      :host {
        display: contents;
      }
      dialog {
        border: 1px solid var(--nf-border);
        border-radius: var(--nf-radius);
        background: var(--nf-surface);
        color: var(--nf-text);
        padding: var(--nf-space-16);
        width: min(480px, 90vw);
      }
      dialog::backdrop {
        background: rgb(0 0 0 / 0.5);
      }
      .header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: var(--nf-space-10);
      }
      form {
        display: flex;
        gap: var(--nf-space-8);
      }
      input {
        flex: 1;
        min-width: 0;
      }
      ul {
        list-style: none;
        padding: 0;
        margin: var(--nf-space-12) 0 0;
      }
      li {
        display: flex;
        align-items: center;
        gap: var(--nf-space-8);
        padding: var(--nf-space-8) 0;
        border-bottom: 1px solid var(--nf-border);
      }
      li:last-child {
        border-bottom: none;
      }
      .candidate {
        flex: 1;
        min-width: 0;
      }
      .title {
        font-weight: 600;
      }
      .url {
        font-size: var(--nf-font-13);
        color: var(--nf-muted);
        word-break: break-all;
      }
      .link {
        background: none;
        border: none;
        color: var(--nf-accent);
      }
    `,
  ];

  private get dialog(): HTMLDialogElement | null {
    return this.shadowRoot?.querySelector("dialog") ?? null;
  }

  /** Open the modal and reset to the URL-entry state. */
  async openDialog(): Promise<void> {
    this.view = "idle";
    this.error = "";
    this.url = "";
    this.candidates = [];
    this.busyUrl = null;
    await this.updateComplete;
    const dialog = this.dialog;
    if (!dialog) return;
    // jsdom (the unit-test environment) implements neither showModal nor close.
    if (typeof dialog.showModal === "function") {
      dialog.showModal();
    } else {
      dialog.setAttribute("open", "");
    }
  }

  /** Close the modal, feature-detecting the same jsdom gap. */
  closeDialog(): void {
    const dialog = this.dialog;
    if (!dialog) return;
    if (typeof dialog.close === "function") {
      dialog.close();
    } else {
      dialog.removeAttribute("open");
    }
  }

  private onUrlInput(event: Event): void {
    if (event.target instanceof HTMLInputElement) this.url = event.target.value;
  }

  private async discover(event: Event): Promise<void> {
    event.preventDefault();
    const raw = this.url.trim();
    if (raw === "" || !this.api) return;
    const url = raw.includes("://") ? raw : `https://${raw}`;
    this.view = "discovering";
    this.error = "";
    this.candidates = [];
    try {
      const candidates = await this.api.discoverFeeds(url);
      if (candidates.length === 0) {
        this.view = "noFeeds";
      } else {
        this.candidates = candidates;
        this.view = "candidates";
      }
    } catch (err) {
      this.view = "discoveryError";
      this.error = err instanceof Error ? err.message : String(err);
    }
  }

  private async subscribe(candidate: FeedCandidate): Promise<void> {
    if (!this.api || this.busyUrl) return;
    this.busyUrl = candidate.url;
    this.error = "";
    try {
      await this.api.subscribe(candidate.url);
      this.closeDialog();
      this.dispatchEvent(
        new CustomEvent("nf-feed-subscribed", {
          bubbles: true,
          composed: true,
        }),
      );
    } catch (err) {
      this.error = err instanceof Error ? err.message : String(err);
    } finally {
      this.busyUrl = null;
    }
  }

  private isSubscribed(url: string): boolean {
    // Exact match mirrors the server's duplicate check (getFeedByUrl).
    return this.feeds.some((f) => f.url === url);
  }

  private renderCandidate(candidate: FeedCandidate): TemplateResult {
    const subscribed = this.isSubscribed(candidate.url);
    const busy = this.busyUrl === candidate.url;
    return html`
      <li>
        <div class="candidate">
          <div class="title">${candidate.title ?? candidate.url}</div>
          <div class="url">${candidate.url}</div>
        </div>
        ${
          subscribed
            ? html`<span class="muted">Subscribed</span>`
            : html`
              <button
                class="primary"
                ?disabled=${this.busyUrl !== null}
                @click=${() => void this.subscribe(candidate)}
              >
                ${busy ? "Adding…" : "Subscribe"}
              </button>
            `
        }
      </li>
    `;
  }

  private renderBody(): TemplateResult {
    switch (this.view) {
      case "discovering":
        return html`<p class="muted">Discovering feeds…</p>`;
      case "discoveryError":
        return html`<p class="error">${this.error}</p>`;
      case "noFeeds":
        return html`<p class="muted">No feeds found on that page.</p>`;
      case "candidates":
        return html`
          ${this.error ? html`<p class="error">${this.error}</p>` : nothing}
          <ul>
            ${this.candidates.map((candidate) => this.renderCandidate(candidate))}
          </ul>
        `;
      default:
        return html``;
    }
  }

  override render(): TemplateResult {
    return html`
      <dialog aria-label="Add feed">
        <div class="header">
          <strong>Add feed</strong>
          <button class="link" @click=${this.closeDialog}>Close</button>
        </div>
        <form @submit=${(e: Event) => void this.discover(e)}>
          <input
            type="text"
            inputmode="url"
            placeholder="example.com or https://example.com"
            aria-label="Feed URL"
            .value=${this.url}
            @input=${this.onUrlInput}
          />
          <button
            type="submit"
            class="primary"
            ?disabled=${this.view === "discovering"}
          >
            Discover
          </button>
        </form>
        ${this.renderBody()}
      </dialog>
    `;
  }
}

customElements.define("nf-add-feed-dialog", NfAddFeedDialog);

declare global {
  interface HTMLElementTagNameMap {
    "nf-add-feed-dialog": NfAddFeedDialog;
  }
}
